import type {
  WidgetCombo,
  WidgetEvent,
  WidgetFunctionsCatalogue,
  WidgetPanel,
  WidgetService,
  WidgetUnit,
} from "../types";
import { useCart } from "../state/cart";
import { comboPricingLabel } from "../state/combos";
import { eventFromPriceCents } from "../state/event-booking-rules";

/**
 * Synthetic non-service tabs the customer can switch to (Functions,
 * Events, etc.). The services_list panel renders these alongside
 * regular services so a "List of services" footer feels complete from
 * the customer's POV — they're tabs in the storefront, even if they're
 * not rows in the `services` table.
 */
export interface PseudoTab {
  id: string;
  name: string;
  tagline: string | null;
}

export interface PanelInteractions {
  /**
   * Called when the body of a cross-sell / service-list row is tapped.
   * WidgetShell uses this to switch the active service tab and stash a
   * "continuing from" context so the destination ServicePanel can show
   * a banner with the previous service's date + party carried forward.
   */
  onSwitchService?: (serviceId: string, fromServiceId: string | null) => void;
  /**
   * Called when the + button on a row is tapped. Adds the cross-sold
   * service unit straight to the cart without leaving the current
   * panel.
   */
  onQuickAdd?: (service: WidgetService, unit: WidgetUnit) => void;
  /**
   * Called when a row in a function_types_list panel is tapped. The
   * shell switches to the Functions tab AND stashes the function type
   * id so FunctionsPanel pre-selects the matching chip — a one-tap
   * "Enquire about a Wedding" deep link.
   */
  onSelectFunctionType?: (functionTypeId: string) => void;
  /**
   * Called when a row in a `packages` panel is tapped. The shell opens
   * the `__packages__` browsing panel — this works even when the
   * Packages TAB is hidden (settings.packages_tab_enabled = false);
   * only the tab bars/lists hide the entry point, never the panel.
   */
  /** Optional combo id — the offer the guest actually clicked, so the packages
   *  panel can open THAT offer rather than its browsing list. Optional so every
   *  existing caller keeps working. */
  onOpenPackages?: (comboId?: string) => void;
}

export function PanelBlock({
  panel,
  services,
  activeService,
  pseudoTabs = [],
  functions = null,
  events = [],
  combos = [],
  packagesLabel = "Packages",
  onSwitchService,
  onQuickAdd,
  onSelectFunctionType,
  onOpenPackages,
}: {
  panel: WidgetPanel;
  services: WidgetService[];
  activeService: WidgetService | null;
  pseudoTabs?: PseudoTab[];
  functions?: WidgetFunctionsCatalogue | null;
  events?: WidgetEvent[];
  /** Bookable combos (WidgetShell's filtered set) for `packages` panels. */
  combos?: WidgetCombo[];
  /** Tenant-set packages title — the fallback heading for `packages` panels. */
  packagesLabel?: string;
} & PanelInteractions) {
  // Matches the prototype's renderSidebarGroup: empty groups are not
  // rendered at all (the prototype JS does `if (items.length === 0)
  // return ''`). Production previously rendered an `<EmptyHint>` for
  // every empty group, which produced the "No upcoming events" /
  // "No cross-sell items" placeholder cards cluttering the sidebar.
  if (
    isPanelEmpty(panel, services, activeService, pseudoTabs, functions, events, combos)
  )
    return null;

  // Panels normally show only a tenant-authored heading; `packages`
  // panels fall back to the tenant's packages title so the block is
  // never an unlabelled list of combo names.
  const heading =
    panel.heading ??
    (panel.source_type === "packages" ? packagesLabel : null);

  return (
    <section className="vt-panel-block">
      {heading && <h3 className="vt-panel-heading">{heading}</h3>}
      {panel.subheading && (
        <p className="vt-panel-subheading">{panel.subheading}</p>
      )}
      <PanelBody
        panel={panel}
        services={services}
        activeService={activeService}
        pseudoTabs={pseudoTabs}
        functions={functions}
        events={events}
        combos={combos}
        onSwitchService={onSwitchService}
        onQuickAdd={onQuickAdd}
        onSelectFunctionType={onSelectFunctionType}
        onOpenPackages={onOpenPackages}
      />
    </section>
  );
}

/**
 * Mirrors the rendering logic of each panel source_type just enough to
 * detect "this panel would render nothing useful". Used to skip the
 * whole `<section>` so empty groups don't crowd the sidebar.
 */
// The admin form labels this field "Max items (0 = show all)", and
// ServicesList / PackagesList / the function-types list honour that. cross_sell,
// featured_units and events did not: they called .slice(0, max_items) directly,
// so a tenant who typed 0 got slice(0, 0) — an EMPTY panel, the exact opposite
// of the promise on the label. One helper so the semantics can't drift again.
function capRows<T>(rows: T[], maxItems: number): T[] {
  return maxItems <= 0 ? rows : rows.slice(0, maxItems);
}

function isPanelEmpty(
  panel: WidgetPanel,
  services: WidgetService[],
  activeService: WidgetService | null,
  pseudoTabs: PseudoTab[] = [],
  functions: WidgetFunctionsCatalogue | null = null,
  events: WidgetEvent[] = [],
  combos: WidgetCombo[] = [],
): boolean {
  switch (panel.source_type) {
    case "cross_sell": {
      const exclude =
        (panel.filter as { exclude_current_service?: boolean })
          ?.exclude_current_service !== false;
      const rows = capRows(
        services
          .filter((s) => !exclude || s.id !== activeService?.id)
          .map((s) => ({ service: s, unit: s.units[0] ?? null })),
        panel.max_items,
      );
      return rows.length === 0;
    }
    case "events":
      return resolveEventRows(panel, services, events).length === 0;
    case "services_list":
      return services.length + pseudoTabs.length === 0;
    case "function_types_list":
      return (functions?.function_types.length ?? 0) === 0;
    case "featured_units": {
      const source = panel.source_service_id
        ? services.find((s) => s.id === panel.source_service_id)
        : null;
      if (!source) return true;
      const ids = (panel.content as { unit_ids?: string[] })?.unit_ids ?? [];
      const filtered =
        ids.length > 0
          ? source.units.filter((u) => ids.includes(u.id))
          : source.units;
      return capRows(filtered, panel.max_items).length === 0;
    }
    case "custom_html": {
      const html = (panel.content as { html?: string })?.html ?? "";
      return html.trim().length === 0;
    }
    case "packages":
      return combos.length === 0;
    default:
      return true;
  }
}

function PanelBody({
  panel,
  services,
  activeService,
  pseudoTabs = [],
  functions = null,
  events = [],
  combos = [],
  onSwitchService,
  onQuickAdd,
  onSelectFunctionType,
  onOpenPackages,
}: {
  panel: WidgetPanel;
  services: WidgetService[];
  activeService: WidgetService | null;
  pseudoTabs?: PseudoTab[];
  functions?: WidgetFunctionsCatalogue | null;
  events?: WidgetEvent[];
  combos?: WidgetCombo[];
} & PanelInteractions) {
  switch (panel.source_type) {
    case "cross_sell":
      return (
        <CrossSellList
          panel={panel}
          services={services}
          activeService={activeService}
          onSwitchService={onSwitchService}
          onQuickAdd={onQuickAdd}
        />
      );
    case "events":
      return (
        <EventsList
          panel={panel}
          services={services}
          events={events}
          onSwitchService={onSwitchService}
          activeService={activeService}
        />
      );
    case "services_list":
      return (
        <ServicesList
          panel={panel}
          services={services}
          activeService={activeService}
          pseudoTabs={pseudoTabs}
          onSwitchService={onSwitchService}
          onQuickAdd={onQuickAdd}
        />
      );
    case "function_types_list":
      return (
        <FunctionTypesList
          panel={panel}
          functions={functions}
          onSelectFunctionType={onSelectFunctionType}
        />
      );
    case "featured_units":
      return (
        <FeaturedUnits
          panel={panel}
          services={services}
          onQuickAdd={onQuickAdd}
        />
      );
    case "custom_html":
      return <CustomHtml panel={panel} />;
    case "packages":
      return (
        <PackagesList
          panel={panel}
          combos={combos}
          onOpenPackages={onOpenPackages}
        />
      );
    default:
      return null;
  }
}

/**
 * A split-card row used across cross-sell / services-list panels. The
 * left side (image + title + price) is a button that switches the
 * widget to the linked service. The right side is a "+" pill that adds
 * the row's primary unit to the cart without leaving the panel. When
 * the row is for the currently-active service, the add button shows ✓
 * and is disabled (the unit is already in the cart for this session).
 */
function SplitRow({
  service,
  unit,
  activeService,
  onSwitchService,
  onQuickAdd,
}: {
  service: WidgetService;
  unit: WidgetUnit | null;
  activeService: WidgetService | null;
} & PanelInteractions) {
  const { items } = useCart();
  const alreadyInCart = unit
    ? items.some(
        (i) => i.serviceId === service.id && i.unitId === unit.id,
      )
    : false;
  const hasPrice =
    service.show_prices_in_widget !== false &&
    unit?.base_price != null &&
    unit.base_price > 0;
  // Subtitle priority (matches the prototype's `.cs-card-body p`):
  // 1. service.tagline if set ("Guided tasting experience") — show
  //    gold "· from R X" price chip beside it,
  // 2. otherwise a "{noun} from R X" fallback that bakes the price in,
  // 3. otherwise just the unit name with the price chip.
  const subtitle = service.tagline
    ? { text: service.tagline, embedsPrice: false }
    : (() => {
        const fb = fallbackSubtitle(service, unit);
        if (fb) return { text: fb, embedsPrice: true };
        if (unit?.name) return { text: unit.name, embedsPrice: false };
        return null;
      })();

  return (
    <li className="vt-panel-row-split">
      <button
        type="button"
        className="vt-panel-row-body-btn"
        onClick={() =>
          onSwitchService?.(service.id, activeService?.id ?? null)
        }
      >
        {/* Matches the prototype's .cs-card markup: title h4 + tagline
            sub-line with inline gold price chip. No media thumbnail. */}
        <span className="vt-panel-row-body">
          <span className="vt-panel-row-title">{service.name}</span>
          {(subtitle || hasPrice) && (
            <span className="vt-panel-row-sub">
              {subtitle?.text}
              {hasPrice && !subtitle?.embedsPrice && (
                <span className="vt-panel-row-price-inline">
                  {subtitle ? " · " : ""}from R {Math.round(unit.base_price!)}
                </span>
              )}
            </span>
          )}
        </span>
      </button>
      {unit && onQuickAdd && (
        <button
          type="button"
          className={`vt-panel-row-add${alreadyInCart ? " vt-panel-row-add-done" : ""}`}
          onClick={() => {
            if (!alreadyInCart) onQuickAdd(service, unit);
          }}
          disabled={alreadyInCart}
          aria-label={alreadyInCart ? "Already added" : `Add ${service.name} to booking`}
          title={alreadyInCart ? "Added" : "Add to booking"}
        >
          {alreadyInCart ? "✓" : "+"}
        </button>
      )}
    </li>
  );
}

// Computed subtitle when the service has no tagline, matching the
// prototype's "Treatments from R 180" / "Tastings from R 175" pattern.
// Returns null if we can't compute a meaningful sub-line.
function fallbackSubtitle(
  service: WidgetService,
  unit: WidgetUnit | null,
): string | null {
  if (
    !unit ||
    service.show_prices_in_widget === false ||
    unit.base_price == null ||
    unit.base_price <= 0
  ) {
    return null;
  }
  const noun =
    service.service_type === "tasting"
      ? "Tastings"
      : service.service_type === "spa"
        ? "Treatments"
        : service.service_type === "events"
          ? "Tickets"
          : service.service_type === "tour"
            ? "Tours"
            : null;
  if (!noun) return null;
  return `${noun} from R ${Math.round(unit.base_price)}`;
}

function CrossSellList({
  panel,
  services,
  activeService,
  onSwitchService,
  onQuickAdd,
}: {
  panel: WidgetPanel;
  services: WidgetService[];
  activeService: WidgetService | null;
} & PanelInteractions) {
  const exclude =
    (panel.filter as { exclude_current_service?: boolean })?.exclude_current_service !==
    false;
  // One row per other service, featuring its first unit WHERE THERE IS ONE.
  //
  // This used to flatMap over s.units.slice(0, 1), so a service with no
  // service_units contributed nothing and could never be cross-sold. That
  // silently excluded exactly the services whose bookables aren't units: SPA
  // (ChiDesk treatments) and THE AUDRIUM (an event-only venue). On Laborie the
  // panel could only ever show 3 cards however high max_items was set.
  //
  // SplitRow already handles a null unit — it hides the "+" quick-add and
  // falls back to the service tagline — which is the right behaviour anyway:
  // a treatment needs the therapist/time picker and an event needs a date, so
  // those cards should route to the tab rather than drop into the cart.
  const rows = capRows(
    services
      .filter((s) => !exclude || s.id !== activeService?.id)
      .map((s) => ({ service: s, unit: s.units[0] ?? null })),
    panel.max_items,
  );

  if (rows.length === 0) return <EmptyHint>No cross-sell items.</EmptyHint>;

  return (
    <ul className="vt-panel-list">
      {rows.map(({ service, unit }) => (
        <SplitRow
          key={service.id}
          service={service}
          unit={unit}
          activeService={activeService}
          onSwitchService={onSwitchService}
          onQuickAdd={onQuickAdd}
        />
      ))}
    </ul>
  );
}

/** Normalised event row rendered by an `events`-source panel, sourced from
 *  either first-class events or (legacy) dated service units. */
interface PanelEventRow {
  key: string;
  name: string;
  startsAt: string;
  location: string | null;
  priceRand: number | null;
  /** Tab the row navigates to when tapped. */
  targetServiceId: string;
}

/**
 * Resolve the rows for an `events`-source panel.
 *
 * Modern events live in `public.events` and reach the widget as the
 * top-level `events` array (WidgetEvent[]) — NOT as `service.units`. The
 * panel used to read only `service.units`, so any tenant on the first-class
 * events model got a silently-empty "Upcoming events" panel (which then
 * hid its whole slot, e.g. the footer). Prefer first-class events; fall
 * back to dated service units so older, unit-modelled events still show.
 */
function resolveEventRows(
  panel: WidgetPanel,
  services: WidgetService[],
  events: WidgetEvent[],
): PanelEventRow[] {
  const dateWindow =
    (panel.filter as { date_window?: string })?.date_window ?? "upcoming_30d";
  const { from, to } = resolveDateWindow(dateWindow);
  const inWindow = (iso: string | null): boolean => {
    if (!iso) return false;
    const ts = new Date(iso).getTime();
    return ts >= from.getTime() && ts < to.getTime();
  };
  const byStart = (a: PanelEventRow, b: PanelEventRow) =>
    new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime();

  const now = Date.now();
  const firstClass: PanelEventRow[] = events
    .filter((e) =>
      panel.source_service_id ? e.service_id === panel.source_service_id : true,
    )
    .filter((e) => inWindow(e.starts_at))
    .map((e) => {
      // The same "from" as the event cards: never the kids menu's price.
      const from = eventFromPriceCents(e, now);
      return {
        key: e.id,
        name: e.name,
        startsAt: e.starts_at,
        location: e.venue_location,
        priceRand: from != null ? Math.round(from / 100) : null,
        // First-class events are bookable on the Events tab.
        targetServiceId: "__events__",
      };
    });
  if (firstClass.length > 0) {
    return capRows(firstClass.sort(byStart), panel.max_items);
  }

  // Legacy: events modelled as dated units on an events-type service.
  const source = panel.source_service_id
    ? services.find((s) => s.id === panel.source_service_id)
    : services.find((s) => s.service_type === "events") ?? null;
  const showPrices = source?.show_prices_in_widget !== false;
  const legacy: PanelEventRow[] = (source?.units ?? [])
    .filter((u) => inWindow(u.starts_at))
    .map((u) => ({
      key: u.id,
      name: u.name,
      startsAt: u.starts_at as string,
      location: u.location_text,
      priceRand:
        showPrices && u.base_price != null && u.base_price > 0
          ? Math.round(u.base_price)
          : null,
      targetServiceId: source?.id ?? "__events__",
    }));
  return capRows(legacy.sort(byStart), panel.max_items);
}

function EventsList({
  panel,
  services,
  events = [],
  onSwitchService,
  activeService,
}: {
  panel: WidgetPanel;
  services: WidgetService[];
  activeService: WidgetService | null;
  events?: WidgetEvent[];
} & PanelInteractions) {
  const rows = resolveEventRows(panel, services, events);
  if (rows.length === 0) return <EmptyHint>No upcoming events.</EmptyHint>;
  const fromId = activeService?.id ?? null;
  return (
    <ul className="vt-panel-list vt-panel-list--event-grid">
      {rows.map((row) => (
        <li key={row.key} className="vt-panel-row-split vt-panel-row-event">
          <button
            type="button"
            className="vt-panel-row-body-btn"
            onClick={() => onSwitchService?.(row.targetServiceId, fromId)}
          >
            {/* Matches the prototype's .cs-event-card: gold uppercase
                date-line, Cormorant title, slate description. */}
            <span className="vt-panel-row-body">
              <span className="vt-panel-row-event-date">
                {formatEventTimestamp(row.startsAt)}
                {row.location && ` · ${row.location}`}
              </span>
              <span className="vt-panel-row-event-name">{row.name}</span>
              {row.priceRand !== null && (
                <span className="vt-panel-row-event-price">
                  from R {row.priceRand}
                </span>
              )}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function resolveDateWindow(w: string): { from: Date; to: Date } {
  const now = new Date();
  const from = new Date(now);
  from.setHours(0, 0, 0, 0);
  const to = new Date(from);

  if (w === "this_weekend") {
    // Day 0 = Sun. Advance `from` to Saturday, `to` to Monday.
    const dow = now.getDay();
    const daysUntilSat = (6 - dow + 7) % 7;
    from.setDate(from.getDate() + daysUntilSat);
    to.setDate(from.getDate() + 2);
    return { from, to };
  }
  if (w === "upcoming_7d") {
    to.setDate(from.getDate() + 7);
    return { from, to };
  }
  // Default: next 30 days
  to.setDate(from.getDate() + 30);
  return { from, to };
}

function formatEventTimestamp(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const day = d.toLocaleDateString("en-ZA", { weekday: "short", day: "numeric", month: "short" });
  const time = d.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${day} · ${time}`;
}

function ServicesList({
  panel,
  services,
  activeService,
  pseudoTabs = [],
  onSwitchService,
  onQuickAdd,
}: {
  panel: WidgetPanel;
  services: WidgetService[];
  activeService: WidgetService | null;
  pseudoTabs?: PseudoTab[];
} & PanelInteractions) {
  // max_items <= 0 means "show all" — the admin form lets us pick 0
  // explicitly, and previously 0 silently rendered an empty list which
  // surprised tenants (their carefully-curated panel showed nothing).
  // Same applies when the customer added more services than they
  // intended to cap.
  const noCap = panel.max_items <= 0;
  const serviceRows = capRows(services, panel.max_items);
  // Reserve room for pseudo-tabs (Functions/Events) — they're cheap to
  // render, customer-meaningful, and shouldn't get squeezed out by a
  // low cap.
  const remaining = noCap
    ? pseudoTabs.length
    : Math.max(0, panel.max_items - serviceRows.length);
  const pseudoRows = noCap ? pseudoTabs : pseudoTabs.slice(0, remaining);
  if (serviceRows.length + pseudoRows.length === 0) return null;
  return (
    <ul className="vt-panel-list">
      {serviceRows.map((s) => (
        <SplitRow
          key={s.id}
          service={s}
          unit={s.units[0] ?? null}
          activeService={activeService}
          onSwitchService={onSwitchService}
          onQuickAdd={onQuickAdd}
        />
      ))}
      {pseudoRows.map((t) => (
        <PseudoTabRow
          key={t.id}
          tab={t}
          onSwitchService={onSwitchService}
          activeService={activeService}
        />
      ))}
    </ul>
  );
}

function PseudoTabRow({
  tab,
  activeService,
  onSwitchService,
}: {
  tab: PseudoTab;
  activeService: WidgetService | null;
  onSwitchService?: PanelInteractions["onSwitchService"];
}) {
  return (
    <li className="vt-panel-row-split">
      <button
        type="button"
        className="vt-panel-row-body-btn"
        onClick={() => onSwitchService?.(tab.id, activeService?.id ?? null)}
      >
        <span className="vt-panel-row-body">
          <span className="vt-panel-row-title">{tab.name}</span>
          {tab.tagline && (
            <span className="vt-panel-row-sub">{tab.tagline}</span>
          )}
        </span>
      </button>
    </li>
  );
}

/**
 * A `packages`-source panel — a curated strip of the tenant's bookable
 * combos (name + description + pricing hook). Every row navigates to the
 * `__packages__` browsing panel via onOpenPackages, which stays reachable
 * even when the Packages TAB is hidden. Same row/collapse conventions as
 * CrossSellList/ServicesList, and the same max_items semantics
 * (<= 0 = show all).
 */
function PackagesList({
  panel,
  combos = [],
  onOpenPackages,
}: {
  panel: WidgetPanel;
  combos?: WidgetCombo[];
  onOpenPackages?: PanelInteractions["onOpenPackages"];
}) {
  const rows = capRows(combos, panel.max_items);
  if (rows.length === 0) return null;
  return (
    <ul className="vt-panel-list">
      {rows.map((combo) => (
        <li key={combo.id} className="vt-panel-row-split">
          <button
            type="button"
            className="vt-panel-row-body-btn"
            onClick={() => onOpenPackages?.(combo.id)}
          >
            <span className="vt-panel-row-body">
              <span className="vt-panel-row-title">{combo.name}</span>
              <span className="vt-panel-row-sub">
                {combo.description}
                <span className="vt-panel-row-price-inline">
                  {combo.description ? " · " : ""}
                  {comboPricingLabel(combo)}
                </span>
              </span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function FeaturedUnits({
  panel,
  services,
  onQuickAdd,
}: {
  panel: WidgetPanel;
  services: WidgetService[];
} & PanelInteractions) {
  const source = panel.source_service_id
    ? services.find((s) => s.id === panel.source_service_id)
    : null;
  if (!source) return <EmptyHint>Pick a source service.</EmptyHint>;
  const ids = (panel.content as { unit_ids?: string[] })?.unit_ids ?? [];
  const filtered =
    ids.length > 0
      ? source.units.filter((u) => ids.includes(u.id))
      : source.units;
  const rows = capRows(filtered, panel.max_items);
  if (rows.length === 0)
    return <EmptyHint>No featured units available.</EmptyHint>;
  const { items } = useCart();
  return (
    <ul className="vt-panel-list">
      {rows.map((u) => {
        const inCart = items.some(
          (i) => i.serviceId === source.id && i.unitId === u.id,
        );
        return (
          <li key={u.id} className="vt-panel-row-split">
            <div className="vt-panel-row-body-btn vt-panel-row-static">
              <span className="vt-panel-row-body">
                <span className="vt-panel-row-title">{u.name}</span>
                {(u.description || u.base_price !== null) && (
                  <span className="vt-panel-row-sub">
                    {u.description}
                    {u.base_price !== null && (
                      <span className="vt-panel-row-price-inline">
                        {u.description ? " · " : ""}R {Math.round(u.base_price)}
                      </span>
                    )}
                  </span>
                )}
              </span>
            </div>
            {onQuickAdd && (
              <button
                type="button"
                className={`vt-panel-row-add${inCart ? " vt-panel-row-add-done" : ""}`}
                onClick={() => {
                  if (!inCart) onQuickAdd(source, u);
                }}
                disabled={inCart}
                aria-label={inCart ? "Already added" : `Add ${u.name} to booking`}
              >
                {inCart ? "✓" : "+"}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function FunctionTypesList({
  panel,
  functions,
  onSelectFunctionType,
}: {
  panel: WidgetPanel;
  functions: WidgetFunctionsCatalogue | null;
  onSelectFunctionType?: PanelInteractions["onSelectFunctionType"];
}) {
  if (!functions || functions.function_types.length === 0) return null;
  // Only show types AT LEAST ONE venue actually hosts — same logic as
  // the chip row inside FunctionsPanel. Surfacing dead types here would
  // dead-end the customer's enquiry.
  const hostedIds = new Set(
    functions.venue_function_types.map((vft) => vft.function_type_id),
  );
  const offered = functions.function_types.filter((ft) => hostedIds.has(ft.id));
  if (offered.length === 0) return null;
  const rows = capRows(offered, panel.max_items);
  return (
    <ul className="vt-panel-list">
      {rows.map((ft) => {
        // Cheapest base price across venue_function_types for this type
        // gives a "from R X" hook; null when no pricing is configured.
        const cheapest = functions.venue_function_types
          .filter((vft) => vft.function_type_id === ft.id)
          .map((vft) => vft.base_price_per_head_cents)
          .filter((c): c is number => c != null && c > 0)
          .sort((a, b) => a - b)[0];
        const subtitle = (() => {
          if (cheapest != null) {
            return `Enquire — from R ${Math.round(cheapest / 100)} per head`;
          }
          if (ft.description) return ft.description;
          return "Enquire about this event";
        })();
        return (
          <li key={ft.id} className="vt-panel-row-split">
            <button
              type="button"
              className="vt-panel-row-body-btn"
              onClick={() => onSelectFunctionType?.(ft.id)}
            >
              <span className="vt-panel-row-body">
                <span className="vt-panel-row-title">{ft.name}</span>
                <span className="vt-panel-row-sub">{subtitle}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function CustomHtml({ panel }: { panel: WidgetPanel }) {
  const html = (panel.content as { html?: string })?.html ?? "";
  if (!html) return null;
  // Tenants control this content via admin; treat as trusted.
  return (
    <div
      className="vt-panel-custom-html"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return <p className="vt-panel-empty">{children}</p>;
}
